import apiClient from './axios';
import { ApiResponse, PaginatedResponse } from '../types';

export type ErrorPlatform = 'backend' | 'mobile';
export type ErrorGroupStatus = 'open' | 'resolved' | 'ignored';

export interface ErrorGroup {
  id: string;
  fingerprint: string;
  platform: ErrorPlatform;
  type: string;
  message: string;
  status: ErrorGroupStatus;
  count: number;
  lastRelease: string | null;
  firstSeenAt: string;
  lastSeenAt: string;
  resolvedAt: string | null;
  resolvedBy: string | null;
  /** What the monitor can say about whether this has stopped. Null on an
   *  error that is still happening, and on one that is not open. */
  fixSignal?: FixSignal | null;
}

/**
 * Evidence that an open error has been fixed.
 *
 * `likely_fixed`: quiet for longer than it ever used to be, with new code
 * out since. `quiet`: a long silence with no sign of new code, which may
 * only mean the error is rare.
 */
export interface FixSignal {
  verdict: 'likely_fixed' | 'quiet';
  quietDays: number;
  usualGapHours: number | null;
  deploysSince: number;
  newCodeAt: string | null;
  newerRelease: string | null;
  /** One sentence, written by the backend, ready to show. */
  reason: string;
}

export interface FixCandidates {
  items: (ErrorGroup & { fixSignal: FixSignal })[];
  likelyFixed: number;
  quiet: number;
}

export interface ErrorOccurrence {
  id: string;
  platform: ErrorPlatform;
  message: string;
  stack: string | null;
  context: string | null;
  release: string | null;
  createdAt: string;
}

export interface GetErrorGroupsParams {
  status?: ErrorGroupStatus;
  platform?: ErrorPlatform;
  page?: number;
  limit?: number;
}

export const monitoringApi = {
  listErrors: async (params: GetErrorGroupsParams = {}) => {
    const { data } = await apiClient.get<ApiResponse<PaginatedResponse<ErrorGroup>>>(
      '/admin/monitoring/errors',
      { params },
    );
    return data.data;
  },

  getError: async (id: string) => {
    const { data } = await apiClient.get<
      ApiResponse<{ group: ErrorGroup; occurrences: ErrorOccurrence[] }>
    >(`/admin/monitoring/errors/${id}`);
    return data.data;
  },

  /** Open errors that look fixed, likely fixed first. */
  fixCandidates: async () => {
    const { data } = await apiClient.get<ApiResponse<FixCandidates>>(
      '/admin/monitoring/errors/fix-candidates',
    );
    return data.data;
  },

  /** Mark several fixed at once. Returns how many were still open. */
  resolveMany: async (ids: string[]) => {
    const { data } = await apiClient.post<ApiResponse<{ resolved: number }>>(
      '/admin/monitoring/errors/resolve',
      { ids },
    );
    return data.data.resolved;
  },

  setStatus: async (id: string, status: ErrorGroupStatus) => {
    const { data } = await apiClient.patch<ApiResponse<ErrorGroup>>(
      `/admin/monitoring/errors/${id}/status`,
      { status },
    );
    return data.data;
  },
};
